import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { CHATBOT_TOKEN_LIMIT_ERROR_MESSAGE } from '@improver/chat/chat-shared';
import { ImproverHttpError } from '@improver/core';
import { AuthService } from '@improver/infrastructure/auth-data-layer';
import { BaseNavigationService, DeniedNavigationService } from '@improver/infrastructure/navigation-data-layer';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { WeblogErrorHandlerService } from '../errors/weblog-error-handler.service';

const EXCLUDED_URLS = ['X-Amz-Signature'];
const MISSING_TOKEN_ERROR_MESSAGE = 'Invalid structure of Authorization bearer header.';
const isChatbotTokenLimitError = (errorResponse: HttpErrorResponse, request: HttpRequest<unknown>): boolean =>
  errorResponse.status === 402 &&
  request.url.includes('/v2/chatbot/') &&
  errorResponse.error?.data?.message === CHATBOT_TOKEN_LIMIT_ERROR_MESSAGE;

const isErrorForWeblog = (error: any): error is ImproverHttpError =>
  error instanceof ImproverHttpError && !error.url.includes('weblog') && String(error.status).startsWith('5');

@Injectable()
export class ErrorHandlerInterceptor implements HttpInterceptor {
  private readonly errorHandlerService: WeblogErrorHandlerService = inject(WeblogErrorHandlerService);
  private readonly baseNavigationService: BaseNavigationService = inject(BaseNavigationService);
  private readonly deniedNavigationService: DeniedNavigationService = inject(DeniedNavigationService);
  private readonly authService: AuthService = inject(AuthService);

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(request).pipe(catchError((errorResponse: HttpErrorResponse) => this.handleError(errorResponse, request)));
  }

  private handleError(errorResponse: HttpErrorResponse, request: HttpRequest<any>): Observable<any> {
    const improverHttpError: ImproverHttpError = ImproverHttpError.newError(request.clone(), errorResponse);

    if (EXCLUDED_URLS.some((url) => request.url.includes(url))) {
      return throwError(() => improverHttpError);
    }

    switch (errorResponse.status) {
      case 402: {
        if (!isChatbotTokenLimitError(errorResponse, request)) {
          this.baseNavigationService.goToAccessExpired();
        }
        break;
      }
      case 403: {
        this.deniedNavigationService.goToDenied();
        break;
      }
      case 400: {
        if (errorResponse.error?.message === MISSING_TOKEN_ERROR_MESSAGE) {
          this.baseNavigationService.goToLogout().then(() => this.authService.logout());
        }
      }
      default:
    }

    if (isErrorForWeblog(improverHttpError)) {
      this.errorHandlerService.sendErrorToWeblog(improverHttpError, improverHttpError.request);
    }

    return throwError(() => improverHttpError);
  }
}
